import React, { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { LineItem } from 'shared/types/cart/LineItem';
import { LineItem as LineItemWishlist } from 'shared/types/wishlist/LineItem';
import ItemDiscontinued from 'components/commercetools-ui/atoms/item-discontinued';
import ItemRestricted from 'components/commercetools-ui/atoms/item-restricted';
import Skeleton from 'components/commercetools-ui/atoms/skeleton';
import StockMessage from 'components/commercetools-ui/atoms/stock-message';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { DateHelpers } from 'helpers/dateHelpers';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import { useAccount, useCart, useWishlist } from 'frontastic';
import Image, { ImageProps } from 'frontastic/lib/image';
import { Money } from 'shared/types/product/Money';
import { useCookies } from 'react-cookie';
import Loading from 'components/commercetools-ui/atoms/loading';
import { formatSize } from 'helpers/constants/sizes';

const attributeMapping: Record<string, string> = {
  waistsize: 'waist',
  leglength: 'leg',
};
const originalOrder = ['colour', 'size', 'shoesize', 'waistsize', 'leglength', 'hand'];

const optionLabelMap: Record<string, string> = {
  waistsize: 'Waist Size',
  leglength: 'Leg Length',
};

interface ClassNames {
  moveToWishlist?: string;
}
interface TaxCategoryRate {
  includedinPrice?: boolean;
  name?: string;
  amount?: number;
}

const labelMapping = {
  Sex: 'Gender',
  IronSets: 'Set Make-Up',
  LoftAdj: 'Loft Adjustment',
  LieAdj: 'Lie Adjustment',
  LengthAdj: 'Length Adjustment',
  GripThickness: 'Grip Thickness',
};
export interface Props {
  item: LineItem;
  taxPortionName?: string;
  productLogo?: ImageProps;
  taxCategoryRate?: TaxCategoryRate;
  classNames?: ClassNames;
  isOnStock?: boolean;
  isDiscontinued?: boolean;
  fluentAvailableQuantity?: number;
  isGiftcard?: boolean;
  isStockChecked?: boolean;
  isExcluded?: boolean;
  isSold?: boolean;
  setHasInsufficientStock?: (arg: boolean) => void;
  onRemove?: (lineItemId: string) => void;
  removing?: boolean;
  onUpdateQuantity?: (lineItemId: string, newQuantity: number) => void;
  updating?: boolean;
}

const CartItem: React.FC<Props> = ({
  item,
  taxPortionName,
  productLogo,
  taxCategoryRate = {},
  classNames = {},
  isOnStock = true,
  isDiscontinued = false,
  fluentAvailableQuantity = 999,
  isGiftcard,
  isStockChecked = false,
  isExcluded = false,
  isSold = false,
  setHasInsufficientStock,
  onRemove,
  removing = false,
  onUpdateQuantity,
  updating = false,
}) => {
  const { ctLocale: locale } = useLocale();
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { removeItem } = useCart();
  const { data: wishlist, addToWishlist } = useWishlist();
  const maxQty = 999;
  const [isMaxQty, setIsMaxQty] = useState(false);
  const isIronsCategory = item?.categoryName?.toLowerCase()?.includes('irons');
  const [mrrpPrice, setMrrpPrice] = useState<any>(null);
  const [cookies] = useCookies(['locale_selection', 'frontastic-session']);

  const { account } = useAccount();

  const counterClassName = useClassNames([
    'flex w-fit items-center gap-12 rounded-md border border-input-border transition hover:border-input-focusBorder p-0 pl-10 pr-10',
    updating || (!isStockChecked && !item.isPerpetualStock)
      ? 'cursor-not-allowed bg-neutral-300'
      : 'cursor-pointer bg-white',
  ]);

  const renderAdditionalInfo = () => {
    const { optionsOrder = [], ...attributes } = item?.variant?.attributes || {};

    if (Object.hasOwn(item, 'selectedCustomFitOptions')) {
      return item?.selectedCustomFitOptions?.map((optionKey: string) => {
        const value = optionKey.split(':');
        const mappedLabel = (labelMapping as any)[value[1]] || value[1];

        // Check if the key contains "_eta"
        // if (optionKey.includes('_eta')) {
        //   // Render ETA in a separate <p> tag
        //   return (
        //     <p key={optionKey} className="mt-4 font-body text-14 font-normal">
        //       {'Estimated dispatch date: '}
        //       <span className="font-nomal">{value[1]}</span>
        //     </p>
        //   );
        // }

        // Render other options in the default UI

        return (
          <div key={optionKey} className="mt-4">
            <span className="text-14 font-light capitalize text-secondary-grey">{mappedLabel}:</span>{' '}
            <span className="text-14 font-light capitalize text-secondary-grey">{value[2]}</span>
          </div>
        );
      });
    }

    const personalizedFields = Object.hasOwn(item, 'personalizedTextFields')
      ? Object.entries(item.personalizedTextFields || {}).map(([key, value]) => {
          const [label = '', text = ''] = value.split(':').map((str: string) => str.trim());
          if (text) {
            return (
              <div key={key} className="mt-4">
                <span className="text-14 font-light capitalize text-secondary-grey">{label}:</span>{' '}
                <span className="text-14 font-light capitalize text-secondary-grey">{text}</span>
              </div>
            );
          }
          return null;
        })
      : [];

    if (!optionsOrder || optionsOrder.length === 0) {
      return null;
    }
    const sortedOptionsOrder = [...optionsOrder]?.sort((a, b) => originalOrder?.indexOf(a) - originalOrder?.indexOf(b));

    // Collect options from the optionsOrder
    const optionsFields = (sortedOptionsOrder as string[])?.map((optionsKey: string) => {
      const mappedKey = attributeMapping?.[optionsKey] || optionsKey;
      const value = attributes?.[optionsKey] ?? attributes?.[mappedKey];
      const optionLabel =
        optionLabelMap?.[optionsKey] || formatCartMessage({ id: optionsKey, defaultMessage: optionsKey });
      if (!value) return null;

      return (
        <div key={optionsKey} className="mt-4">
          <span className="text-14 font-light capitalize text-secondary-grey">{optionLabel?.replace(/_/g, ' ')}:</span>{' '}
          <span className="text-14 font-light capitalize text-secondary-grey">
            {optionsKey?.toLowerCase() === 'size' ? formatSize(value) : value}
          </span>
        </div>
      );
    });
    // Combine and return both arrays of fields
    return [...optionsFields, ...personalizedFields];
  };

  const cartLineItemToWishlistLineItem = useMemo<LineItemWishlist>(() => {
    return {
      lineItemId: item.lineItemId ?? '',
      productId: item.productId,
      name: item.name,
      type: item.type,
      count: 1,
      variant: item.variant,
      addedAt: new Date(),
      _url: item._url,
    };
  }, [item]);

  const moveToWishlist = useCallback(async () => {
    if (item.lineItemId) await removeItem(item.lineItemId);
    addToWishlist(wishlist, cartLineItemToWishlistLineItem, 1);
  }, [removeItem, item.lineItemId, addToWishlist, wishlist, cartLineItemToWishlistLineItem]);

  // useEffect(() => {
  //   (async () => {
  //     if (item.count) {
  //       if (item.count > maxQty) {
  //         await updateItem(item.lineItemId as string, maxQty);
  //       }
  //     }
  //   })();
  // }, [item.count]);

  useEffect(() => {
    if (item?.variant?.attributes?.standalonePrice) {
      try {
        const standalonePrice = JSON.parse(item?.variant.attributes.standalonePrice);
        const discountedPrices =
          standalonePrice?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU']?.custom || null;
        if (discountedPrices?.mrrp) {
          setMrrpPrice(discountedPrices?.mrrp);
        } else {
          setMrrpPrice(null);
        }
      } catch (error) {
        console.error('Error parsing standalonePrice:', error);
      }
    }
  }, [item?.variant, cookies?.locale_selection?.currency]);

  return (
    <div className="flex max-w-full items-stretch justify-start gap-10 py-18 md:gap-15">
      <div className="p-[30px 10px 30px 0] m-0 w-[90px] shrink-0 bg-white md:w-[130px]">
        <a href={item._url ?? '#'}>
          <div className="relative h-full w-full rounded-sm">
            <Image
              src={item.variant?.images?.[0] || productLogo?.media?.file}
              suffix="small"
              style={{ objectFit: 'contain' }}
              fill
              alt={item.name}
            />
          </div>
        </a>
      </div>
      <div className="max-w-full grow overflow-hidden pl-[10px]">
        <div className="flex items-center justify-between">
          <a href={item._url ?? '#'} className="w-[80%]">
            <p
              className="truncate whitespace-nowrap text-14 leading-loose text-link-normal transition-colors hover:text-link-hover"
              title={item.name}
            >
              {item.name}
            </p>
          </a>

          <span
            onClick={() => !removing && onRemove && onRemove(item?.lineItemId ?? '')}
            className={`mt-12 hidden text-13 font-light underline md:block ${
              !isStockChecked && !item.isPerpetualStock && !isSold
                ? 'cursor-not-allowed text-neutral-500'
                : 'cursor-pointer text-black'
            }`}
          >
            {removing ? 'Removing...' : 'Remove'}
          </span>
        </div>

        {isDiscontinued && (
          <div className="mb-4 mt-8">
            <ItemDiscontinued />
          </div>
        )}

        {isExcluded && (
          <div className="mb-4 mt-8">
            <ItemRestricted />
          </div>
        )}

        {isStockChecked && (fluentAvailableQuantity || !item.isPerpetualStock) && (
          <StockMessage
            isOnStock={isOnStock}
            count={item.count}
            fluentAvailableQuantity={fluentAvailableQuantity}
            isPerpetualStock={item.isPerpetualStock}
          />
        )}

        <div className="mt-8">
          {mrrpPrice &&
          mrrpPrice?.centAmount &&
          mrrpPrice?.centAmount !== item?.price?.centAmount &&
          mrrpPrice?.centAmount > (item?.price?.centAmount ?? 0) ? (
            <div className="flex items-center gap-5">
              <span className="text-14 font-normal leading-loose text-gray-500 line-through">
                {CurrencyHelpers.formatForCurrency(
                  mrrpPrice.centAmount,
                  locale,
                  mrrpPrice.currencyCode,
                  mrrpPrice.fractionDigits,
                )}
              </span>
              <span className="text-20 font-medium leading-loose text-accent-red">
                {CurrencyHelpers.formatForCurrency(item.price ?? 0, locale)}
              </span>
            </div>
          ) : (
            <span className="text-20 font-medium leading-loose">
              {CurrencyHelpers.formatForCurrency(item.price ?? 0, locale)}
            </span>
          )}
          <div className="text-12 font-light capitalize text-secondary-grey">
            {taxPortionName ? (
              <>
                {formatCartMessage({ id: 'vat.sales.tax', defaultMessage: 'VAT / Sales tax' })} {taxPortionName}
              </>
            ) : (
              <>
                {formatCartMessage({ id: 'vat.sales.tax', defaultMessage: 'VAT / Sales tax' })} {taxCategoryRate?.name}
              </>
            )}
          </div>
        </div>

        {renderAdditionalInfo()}

        <div className="mt-2">
          {item.discountTexts?.map((text, index) => (
            <span key={index} className="text-14 capitalize italic text-accent-red">
              <div>{text}</div>
            </span>
          )) ?? ''}
        </div>
        {isIronsCategory && (
          <span className="bold text-14 italic text-accent-red">{`${formatCartMessage({
            id: 'cart.ironText',
            defaultMessage: 'Total amount is calculated based on Set Make- Up Irons.',
          })}`}</span>
        )}
        {!isGiftcard && !isExcluded && !isSold && (
          <div className="mt-16">
            <div className={counterClassName}>
              {updating ? (
                <>
                  <Loading />
                </>
              ) : (
                <>
                  {isIronsCategory ? (
                    <span className="relative top-2 block px-4 py-12 text-14 text-secondary-black">{item?.count}</span>
                  ) : (
                    <span className="relative top-2 px-4 text-14 text-secondary-black">{item?.count}</span>
                  )}
                  {!isIronsCategory && (
                    <span className="inline border-l">
                      <button
                        onClick={() => {
                          if (!item?.lineItemId || !onUpdateQuantity) return;
                          onUpdateQuantity(item.lineItemId, (item?.count ?? 0) + 1);
                        }}
                        className={`block cursor-[inherit] p-4 py-1 pl-10 ${
                          isMaxQty || item.count === 999 ? 'cursor-not-allowed text-gray-300' : 'text-green-500'
                        }`}
                        aria-label="Increase Quantity"
                        disabled={isMaxQty}
                      >
                        <i className="icon icon-arrow_up relative text-10"></i>
                      </button>
                      <button
                        onClick={() => {
                          if (!item?.lineItemId || !onUpdateQuantity) return;
                          onUpdateQuantity(item.lineItemId, (item?.count ?? 1) - 1);
                        }}
                        className="block cursor-[inherit] p-4 py-1 pl-10 text-green-500"
                        aria-label="Decrease Quantity"
                        disabled={
                          item?.variant?.attributes?.minQty
                            ? Number(item?.variant?.attributes?.minQty) === item.count
                            : item.count === 1
                        }
                      >
                        <i className="icon icon-arrow_down relative text-10"></i>
                      </button>
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {item.variant?.attributes?.isPreorder &&
          (() => {
            const preOrderDate = DateHelpers.formatDateDayMonth(item.variant?.attributes?.preOrderDate);
            if (!preOrderDate) return null;

            return <p className="mt-14 text-14 text-ag_color-text_green">Pre Order Item - Shipping {preOrderDate}</p>;
          })()}

        {isGiftcard && (
          <>
            <div className="mt-6 text-12 font-light capitalize text-secondary-grey">
              <span>{`${formatCartMessage({
                id: 'cart.giftcard.delivery.via',
                defaultMessage: 'Delivery via',
              })} ${item.giftcardAttrs?.type === 'ONLINE' ? 'email' : 'post'}`}</span>
            </div>
            {item.giftcardAttrs?.type === 'ONLINE' && (
              <div className="mt-6 text-12 font-light text-secondary-grey">
                <span className="font-light">{`${formatCartMessage({
                  id: 'cart.giftcard.recipients.name',
                  defaultMessage: "Recipient's name",
                })}: `}</span>
                <span>{item.giftcardAttrs?.recipientName}</span>
              </div>
            )}
            {item.giftcardAttrs?.type === 'ONLINE' && (
              <>
                <div className="mt-6 text-12 font-light text-secondary-grey">
                  <span className="font-light">{`${formatCartMessage({
                    id: 'cart.giftcard.delivery.email',
                    defaultMessage: 'Email',
                  })}: `}</span>
                  <span>{item.giftcardAttrs?.recipientEmail}</span>
                </div>
              </>
            )}
          </>
        )}

        {item.variant?.attributes?.minQty && (
          <div className="mt-16">
            <span className="flex h-[25px] w-fit items-center justify-center bg-ag_color-warning px-8 py-4 pt-6 text-12 text-white">{`${formatCartMessage(
              {
                id: 'minimum.order.quantity',
                defaultMessage: 'Minimum order quantity',
              },
            )}: ${item.variant?.attributes?.minQty}`}</span>
          </div>
        )}

        <div className="mt-16 w-fit text-12">
          {account && isStockChecked && (
            <p
              className={`cursor-pointer text-secondary-black decoration-secondary-black decoration-solid hover:text-[#666] hover:underline hover:underline-offset-2 ${
                classNames.moveToWishlist ?? ''
              }`}
              onClick={moveToWishlist}
            >
              {formatCartMessage({ id: 'move.to.wishlist', defaultMessage: 'Move to wishlist' })}
            </p>
          )}
          {account && !isStockChecked && !item.isPerpetualStock && (
            <span className="relative mt-12 hidden h-[15px] w-[100px] cursor-none md:block">
              <Skeleton></Skeleton>
            </span>
          )}
          <span
            onClick={() => !removing && onRemove && onRemove(item?.lineItemId ?? '')}
            className={`mt-12 block text-13 font-light underline md:hidden ${
              !isStockChecked && !item.isPerpetualStock && !isSold
                ? 'cursor-not-allowed text-neutral-500'
                : 'cursor-pointer text-black'
            }`}
          >
            {removing ? 'Removing...' : 'Remove'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
